/**
 * Motor de exercícios rítmicos, reutilizável por qualquer aula de ritmo.
 *
 * Ele recebe a "receita" da aula (notas permitidas, figuras, compasso, BPM,
 * número de compassos, modo de resposta e quantidade de exercícios) e devolve
 * sequências válidas — sempre dentro do conteúdo já ensinado. Nada de notas,
 * figuras ou acidentes que o aluno ainda não viu.
 */

import { getNote } from '@/lib/music/notes';
import { DURATIONS, type DurationId } from '@/lib/music/rhythm';
import { pickNextNoteId, pushRecent, type NoteStatsMap } from '@/lib/exercise/engine';

export type RhythmResponseMode = 'pulse' | 'on-beat';

export interface RhythmPatternEvent {
  kind: 'note' | 'rest';
  durationId: DurationId;
  noteId?: string;
}

export interface RhythmConfig {
  /** notas permitidas (ids, ex: ["C4","D4"]) */
  notes: string[];
  /** figuras rítmicas permitidas */
  figures: DurationId[];
  /** pausas permitidas (opcional; aulas de pausa) */
  rests?: DurationId[];
  /**
   * Probabilidade de cada figura virar pausa quando a aula permite pausas.
   * Aulas focadas em pausa usam um valor mais alto (ex.: 0.4).
   */
  restChance?: number;
  /** pulsos por compasso (4 = 4/4, 3 = 3/4, 2 = 2/4) */
  beatsPerMeasure: number;
  /**
   * Lista de compassos usada para alternar a fórmula a cada exercício
   * (ex.: [2, 3, 4] na aula de comparação). Quando ausente, vale beatsPerMeasure.
   */
  timeSignatures?: number[];
  /** andamento de referência */
  bpm: number;
  /** quantos compassos tem cada exercício */
  bars: number;
  /** pulse = marcar o pulso com qualquer tecla; on-beat = tocar a nota certa no pulso */
  responseMode: RhythmResponseMode;
  /** quantos exercícios para concluir a aula */
  exercises: number;
  /**
   * Sequência fixa opcional. Quando presente, transforma a aula em uma pequena
   * partitura real que pode ser repetida até o domínio, em vez de gerar notas
   * aleatórias. Útil para desafios finais e futuras músicas de treino.
   */
  fixedSequence?: RhythmPatternEvent[];
  /** pulsos de contagem inicial antes de tocar */
  countIn?: number;
  /**
   * Liga uma dica visual na pauta: a região da pausa "acende" durante todo o
   * silêncio, reforçando que ali não se toca. Usada nas aulas de pausa.
   */
  restGlow?: boolean;
  /**
   * Movimento melódico pretendido (aulas de direção e de saltos):
   * 'free' = sorteio livre; 'stepwise' = sempre para a nota vizinha, subindo e
   * descendo; 'jumps' = saltos de 2 ou 3 graus, sem passar pela vizinha.
   */
  motion?: 'free' | 'stepwise' | 'jumps';
  /**
   * Liga a ligadura de duração: uma nota no fim do compasso é ligada à primeira
   * nota do compasso seguinte (mesma altura), somando as durações em um só som.
   */
  ties?: boolean;
}

export interface RhythmNoteEvent {
  kind: 'note';
  noteId: string;
  durationId: DurationId;
  beats: number;
  startBeat: number;
  /**
   * Continuação de uma ligadura: esta nota não é atacada de novo — o som já
   * vinha da nota anterior e apenas se prolonga por ela.
   */
  tied?: boolean;
}

export interface RhythmRestEvent {
  kind: 'rest';
  durationId: DurationId;
  beats: number;
  startBeat: number;
}

export type RhythmEvent = RhythmNoteEvent | RhythmRestEvent;

export interface RhythmExercise {
  id: string;
  events: RhythmEvent[];
  totalBeats: number;
  bars: number;
  beatsPerMeasure: number;
}

const UNIT = 0.25;

function toUnits(beats: number): number {
  return Math.round(beats / UNIT);
}

function fromUnits(units: number): number {
  return units * UNIT;
}

/** Pequeno gerador pseudoaleatório determinístico (mesma semente → mesmo exercício). */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Dá para completar exatamente `targetUnits` usando apenas as durações dadas? */
function canFill(targetUnits: number, allowedUnits: number[], memo = new Map<number, boolean>()): boolean {
  if (targetUnits === 0) return true;
  if (targetUnits < 0) return false;
  const cached = memo.get(targetUnits);
  if (cached !== undefined) return cached;
  let ok = false;
  for (const unit of allowedUnits) {
    if (unit <= targetUnits && canFill(targetUnits - unit, allowedUnits, memo)) {
      ok = true;
      break;
    }
  }
  memo.set(targetUnits, ok);
  return ok;
}

function figForBeats(figures: DurationId[], beats: number): DurationId {
  return figures.find((id) => DURATIONS[id].beats === beats) ?? 'quarter';
}

/**
 * Gera uma sequência rítmica válida para a aula.
 * Se `stats` for informado, as notas são escolhidas de forma adaptativa
 * (as que o aluno mais erra aparecem com mais frequência).
 */
export function buildRhythmExercise(
  config: RhythmConfig,
  seed: number,
  stats?: NoteStatsMap,
): RhythmExercise {
  const rng = mulberry32(seed);
  const measureUnits = toUnits(config.beatsPerMeasure);

  if (config.fixedSequence && config.fixedSequence.length > 0) {
    let startBeat = 0;
    const events: RhythmEvent[] = config.fixedSequence.map((item) => {
      const beats = DURATIONS[item.durationId].beats;
      const event: RhythmEvent =
        item.kind === 'rest'
          ? { kind: 'rest', durationId: item.durationId, beats, startBeat }
          : {
              kind: 'note',
              noteId: item.noteId ?? config.notes[0] ?? 'C4',
              durationId: item.durationId,
              beats,
              startBeat,
            };
      startBeat += beats;
      return event;
    });
    return {
      id: `fixed-${seed}`,
      events,
      totalBeats: startBeat,
      bars: Math.max(1, Math.ceil(startBeat / config.beatsPerMeasure)),
      beatsPerMeasure: config.beatsPerMeasure,
    };
  }
  const totalUnits = measureUnits * config.bars;
  // Cada figura precisa caber no compasso. Não exigimos que ela preencha o
  // compasso sozinha (a mínima pontuada, por exemplo, não preenche 4/4 sozinha),
  // senão figuras com ponto de aumento seriam descartadas. O que importa é que
  // o CONJUNTO de figuras consiga preencher o compasso exato.
  const rawUnits = Array.from(
    new Set(config.figures.map((f) => toUnits(DURATIONS[f].beats))),
  ).filter((units) => units > 0 && units <= measureUnits);
  const noteUnits = rawUnits.length > 0 && canFill(measureUnits, rawUnits) ? rawUnits : [toUnits(1)];
  const allowedUnits = noteUnits.length > 0 ? noteUnits : [toUnits(1)];
  const allowedNotes = config.notes.length > 0 ? config.notes : ['C4'];
  const restChance = config.restChance ?? 0.18;

  const events: RhythmEvent[] = [];
  const recent: string[] = [];
  let lastNoteId: string | null = null;
  let unit = 0;

  // Estado do movimento melódico (aulas de direção e de saltos).
  const motion = config.motion ?? 'free';
  const motionNotes = [...allowedNotes].sort((a, b) => getNote(a).midi - getNote(b).midi);
  let motionIndex = Math.floor(rng() * Math.max(1, motionNotes.length));
  let motionDir: 1 | -1 = rng() < 0.5 ? -1 : 1;
  const pickMotionNote = (): string => {
    const maxIdx = motionNotes.length - 1;
    const step = motion === 'jumps' ? (rng() < 0.5 ? 2 : 3) : 1;
    let next = motionIndex + motionDir * step;
    if (next > maxIdx || next < 0) {
      motionDir = motionDir === 1 ? -1 : 1;
      next = motionIndex + motionDir * step;
      if (next > maxIdx) next = maxIdx;
      if (next < 0) next = 0;
    }
    motionIndex = next;
    return motionNotes[next];
  };

  while (unit < totalUnits) {
    const measurePos = unit % measureUnits;
    const remainingInMeasure = measureUnits - measurePos;
    const candidates = allowedUnits.filter((u) => u <= remainingInMeasure && canFill(remainingInMeasure - u, allowedUnits));
    const pool = candidates.length > 0 ? candidates : allowedUnits.filter((u) => u <= remainingInMeasure);
    const chosen = pool[Math.floor(rng() * pool.length)];
    const beats = fromUnits(chosen);
    const durationId = figForBeats(config.figures, beats);

    const restUnitsMatch = (config.rests ?? []).filter((r) => toUnits(DURATIONS[r].beats) === chosen);
    const previousIsRest = events.length > 0 && events[events.length - 1].kind === 'rest';
    const isRest = restUnitsMatch.length > 0 && events.length > 0 && !previousIsRest && rng() < restChance;

    if (isRest) {
      const restId = restUnitsMatch[Math.floor(rng() * restUnitsMatch.length)];
      events.push({ kind: 'rest', durationId: restId, beats, startBeat: fromUnits(unit) });
    } else {
      let noteId: string;
      if (motion !== 'free' && motionNotes.length > 1) {
        noteId = pickMotionNote();
      } else if (stats && allowedNotes.length > 1) {
        noteId = pickNextNoteId(allowedNotes, stats, recent);
        recent.splice(0, recent.length, ...pushRecent(recent, noteId));
      } else {
        noteId = allowedNotes[Math.floor(rng() * allowedNotes.length)];
        if (allowedNotes.length > 1 && noteId === lastNoteId) {
          const alternatives = allowedNotes.filter((id) => id !== lastNoteId);
          noteId = alternatives[Math.floor(rng() * alternatives.length)];
        }
      }
      lastNoteId = noteId;
      events.push({ kind: 'note', noteId, durationId, beats, startBeat: fromUnits(unit) });
    }

    unit += chosen;
  }

  // Aulas que ensinam pausa precisam que ela realmente apareça: se o sorteio
  // não trouxe nenhuma, transformamos uma figura em pausa (sem mudar o tempo).
  if ((config.rests ?? []).length > 0 && !events.some((event) => event.kind === 'rest')) {
    for (let i = 1; i < events.length; i += 1) {
      const candidate = events[i];
      const restId = (config.rests ?? []).find(
        (r) => toUnits(DURATIONS[r].beats) === toUnits(candidate.beats),
      );
      if (restId) {
        events[i] = {
          kind: 'rest',
          durationId: restId,
          beats: candidate.beats,
          startBeat: candidate.startBeat,
        };
        break;
      }
    }
  }

  // Ligadura de duração: procuramos uma nota que termine exatamente no fim de um
  // compasso e a ligamos à primeira nota do compasso seguinte (mesma altura).
  // Assim as duas durações se somam em um único som atravessando a barra.
  if (config.ties) {
    for (let i = 0; i + 1 < events.length; i += 1) {
      const current = events[i];
      const next = events[i + 1];
      if (current.kind !== 'note' || next.kind !== 'note') continue;
      const endUnit = toUnits(current.startBeat) + toUnits(current.beats);
      if (endUnit % measureUnits !== 0) continue;
      events[i + 1] = { ...next, noteId: current.noteId, tied: true };
      break;
    }
  }

  return {
    id: `rx-${seed}`,
    events,
    totalBeats: fromUnits(totalUnits),
    bars: config.bars,
    beatsPerMeasure: config.beatsPerMeasure,
  };
}

export interface RhythmScore {
  correct: number;
  early: number;
  late: number;
  wrongNote: number;
  /** notas escritas (não ligadas) que nunca foram atacadas no seu tempo */
  missedNotes: number;
  /** toques indevidos em silêncio/ligação (soma de restIntrusion + tieReattack) */
  silence: number;
  /** quantas pausas o aluno respeitou (ficou em silêncio no lugar certo) */
  restsHeld: number;
  /** quantas ligaduras o aluno manteve (segurou o som em vez de atacar de novo) */
  tiesHeld: number;
  attempts: number;
  /** toques em cima de uma pausa (o silêncio foi quebrado) */
  restIntrusion: number;
  /** reataques sobre uma nota ligada (a ligadura foi quebrada) */
  tieReattack: number;
  /** toques que cortaram uma figura antes do fim da sua duração */
  durationBreak: number;
  /**
   * notas cuja altura certa foi atacada e cuja tecla foi SOLTA depois da
   * duração exigida (medição explícita pelo fim da nota — soltar a tecla no
   * teclado virtual ou Note Off no MIDI).
   */
  durationsHeld: number;
  /**
   * notas que continuavam pressionadas quando a figura terminou. Isso é uma
   * sustentação plausível, mas só vira acerto se o aparelho tiver comprovado
   * que informa o fim da nota (`noteOffCount > 0`); caso contrário não é
   * mensurável e não pode ser dado como certo.
   */
  heldToEnd: number;
  /** quantas mensagens de fim de nota (note off) foram recebidas na sessão */
  noteOffCount: number;
}

export function emptyRhythmScore(): RhythmScore {
  return {
    correct: 0,
    early: 0,
    late: 0,
    wrongNote: 0,
    missedNotes: 0,
    silence: 0,
    restsHeld: 0,
    tiesHeld: 0,
    attempts: 0,
    restIntrusion: 0,
    tieReattack: 0,
    durationBreak: 0,
    durationsHeld: 0,
    heldToEnd: 0,
    noteOffCount: 0,
  };
}

/** Resumo avaliado de UM exercício: notas, ataque, duração, pausa e ligadura. */
export interface ExerciseSummary {
  index: number;
  timeSignature: string;
  score: RhythmScore;
}

/** Diferença entre dois placares (usada para isolar o resultado de cada exercício). */
export function scoreDelta(before: RhythmScore, after: RhythmScore): RhythmScore {
  return {
    correct: after.correct - before.correct,
    early: after.early - before.early,
    late: after.late - before.late,
    wrongNote: after.wrongNote - before.wrongNote,
    missedNotes: after.missedNotes - before.missedNotes,
    silence: after.silence - before.silence,
    restsHeld: after.restsHeld - before.restsHeld,
    tiesHeld: after.tiesHeld - before.tiesHeld,
    attempts: after.attempts - before.attempts,
    restIntrusion: after.restIntrusion - before.restIntrusion,
    tieReattack: after.tieReattack - before.tieReattack,
    durationBreak: after.durationBreak - before.durationBreak,
    durationsHeld: after.durationsHeld - before.durationsHeld,
    heldToEnd: after.heldToEnd - before.heldToEnd,
    noteOffCount: after.noteOffCount - before.noteOffCount,
  };
}

export function rhythmAccuracy(score: RhythmScore): number {
  if (score.attempts === 0) return 0;
  return Math.round((score.correct / score.attempts) * 100);
}

/**
 * Notas atacadas de um exercício (usadas para tocar o exemplo e para revisão).
 * As continuações de ligadura ficam de fora, porque não são atacadas de novo.
 */
export function exerciseNoteIds(exercise: RhythmExercise): string[] {
  return exercise.events
    .filter((event): event is RhythmNoteEvent => event.kind === 'note' && !event.tied)
    .map((event) => event.noteId);
}

export function exerciseMidis(exercise: RhythmExercise): number[] {
  return exerciseNoteIds(exercise).map((id) => getNote(id).midi);
}

/**
 * Regra EXATA de duração (a mesma usada pelo motor e testada em /diagnostico).
 *
 * A tecla precisa permanecer pressionada praticamente até o fim da figura
 * escrita. A tolerância é pequena e explícita, só para absorver atraso humano e
 * latência do aparelho (teclado virtual ou MIDI):
 *   - exige sustentar pelo menos 80% da duração escrita; e
 *   - nunca aceita soltar mais de 0,25 pulso antes do fim (um quarto de pulso).
 * Vale a maior das duas exigências (a mais rígida). Exemplos:
 *   - mínima (2 pulsos): precisa segurar ≥ 1,75 pulso; soltar em 1 pulso = ERRO.
 *   - semibreve (4 pulsos): precisa segurar ≥ 3,75 pulsos.
 *   - semicolcheia (0,25 pulso): precisa segurar ≥ 0,20 pulso.
 * Aplica-se ao grupo todo: nota-base somada às suas ligaduras, quando houver.
 */
export const DURATION_MIN_HOLD_RATIO = 0.8;
export const DURATION_MAX_EARLY_BEATS = 0.25;

/** Duração mínima (em pulsos) que um grupo precisa ser sustentado. */
export function minHoldBeats(requiredBeats: number): number {
  return Math.max(requiredBeats * DURATION_MIN_HOLD_RATIO, requiredBeats - DURATION_MAX_EARLY_BEATS);
}

/** A tecla foi segurada o suficiente para a duração contar como certa? */
export function isHoldLongEnough(heldBeats: number, requiredBeats: number): boolean {
  return requiredBeats > 0 && heldBeats + 1e-6 >= minHoldBeats(requiredBeats);
}